/**
 * PricingMobileCards Component
 * 
 * Mobile-friendly card layout for pricing display (< 640px screens).
 * Works with both DMC pricing options and meal plan categories.
 * 
 * Features:
 * - Stacked card layout
 * - Supports person_wise and total formats (matches canonical vocabulary)
 * - Respects showBreakup for Hotel/Transfer display
 * - Dynamic pax/discount display
 * - GST handling (included vs separate)
 * 
 * Usage:
 *   <PricingMobileCards
 *     type="meal_plan"
 *     mealPlanLabel="Breakfast Only"
 *     categories={[...]}
 *     format="person_wise"
 *     showBreakup={true}
 *     paxConfig={{ adults: 2, extraBed: 0, childWithBed: 1, childNoBed: 0, infants: 0 }}
 *     gstConfig={{ percentage: 5, included: false }}
 *   />
 */

import React from 'react';
import { formatCurrency } from './displayConfig';

const PricingMobileCards = ({
  type = 'meal_plan', // 'meal_plan' or 'dmc'
  mealPlanLabel = '',
  categories = [],
  options = [], // For DMC type
  format = 'total',
  showBreakup = true,
  paxConfig = {},
  gstConfig = {},
  currencyConfig = {}, // For DMC
  className = ''
}) => {
  const items = type === 'dmc' ? options : categories;
  
  if (!items || items.length === 0) {
    return null;
  }

  // Ticket #285275 (#9/#10): saved quotes store 'per_person', the form uses 'person_wise' - accept both.
  const isPersonWise = format === 'person_wise' || format === 'per_person';
  
  // Pax configuration
  const { 
    adults = 0, 
    adultsInRooms = adults,
    extraBed = 0, 
    childWithBed = 0, 
    childNoBed = 0, 
    infants = 0 
  } = paxConfig;
  
  // GST configuration
  const { 
    percentage: gstPct = 5, 
    included: gstIncluded = false,
    applyOn = 'total'
  } = gstConfig;
  
  // Currency configuration (for DMC)
  const { 
    currency = 'INR', 
    conversionRate = 1, 
    markupPct = 0 
  } = currencyConfig;
  const needsConversion = currency !== 'INR' && conversionRate > 1;
  const markupMultiplier = 1 + (markupPct / 100);

  // Render a single card for person_wise format
  const renderPersonWiseCard = (item, idx) => {
    let adultPrice, extraBedPrice, childBedPrice, childNoBedPrice, infantPrice;
    
    if (type === 'dmc') {
      const rate = needsConversion ? conversionRate : 1;
      adultPrice = (item.per_person || 0) * rate * markupMultiplier;
      extraBedPrice = (item.extra_adult || 0) * rate * markupMultiplier;
      childBedPrice = (item.child_with_bed || 0) * rate * markupMultiplier;
      childNoBedPrice = (item.child_no_bed || 0) * rate * markupMultiplier;
      infantPrice = (item.infant || 0) * rate * markupMultiplier;
    } else {
      adultPrice = item.adultPrice || item.adult_price || item.per_person_price || 0;
      extraBedPrice = item.extraBedPrice || item.adult_extra_bed_price || 0;
      childBedPrice = item.childBedPrice || item.child_with_bed_price || 0;
      childNoBedPrice = item.childNoBedPrice || item.child_no_bed_price || 0;
      infantPrice = item.infantPrice || item.infant_price || 0;
    }
    
    const actualAdults = type === 'dmc' ? adultsInRooms : adults;
    const total = (adultPrice * actualAdults) +
                 (extraBedPrice * extraBed) +
                 (childBedPrice * childWithBed) +
                 (childNoBedPrice * childNoBed) +
                 (infantPrice * infants);
    
    const discount = (item.discount || 0) * (type === 'dmc' && needsConversion ? conversionRate * markupMultiplier : 1);
    const afterDiscount = total - discount;
    const gstAmount = gstPct > 0 ? afterDiscount * (gstPct / 100) : 0;
    // Ticket #285275 (Option A): included + stored GST > 0 means GST was added.
    const storedGst = Number(item.gst_amount || item.gstAmount || 0);
    const gstInTotal = gstIncluded && storedGst > 0;
    const grandTotal = (gstIncluded && !gstInTotal) ? afterDiscount : afterDiscount + (gstInTotal ? storedGst : gstAmount);
    
    const itemName = item.option_name || item.name || item.category || `Option ${idx + 1}`;
    
    return (
      <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="font-semibold text-[var(--navy)] mb-2">{itemName.replace(/_/g, ' ')}</div>
        
        <div className="space-y-2">
          {/* Adult */}
          <div className="flex justify-between text-sm">
            <span className="text-[var(--navy)]">Adult x{actualAdults}</span>
            <span className="font-semibold">{formatCurrency(adultPrice)}</span>
          </div>
          
          {/* Extra Bed */}
          {extraBed > 0 && extraBedPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--navy)]">Extra Bed x{extraBed}</span>
              <span className="font-semibold">{formatCurrency(extraBedPrice)}</span>
            </div>
          )}
          
          {/* Child with Bed */}
          {childWithBed > 0 && childBedPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--navy)]">Child (Bed) x{childWithBed}</span>
              <span className="font-semibold">{formatCurrency(childBedPrice)}</span>
            </div>
          )}
          
          {/* Child without Bed */}
          {childNoBed > 0 && childNoBedPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--navy)]">Child (No Bed) x{childNoBed}</span>
              <span className="font-semibold">{formatCurrency(childNoBedPrice)}</span>
            </div>
          )}
          
          {/* Infant */}
          {infants > 0 && infantPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--navy)]">Infant x{infants}</span>
              <span className="font-semibold">{formatCurrency(infantPrice)}</span>
            </div>
          )}
          
          {/* Total */}
          <div className="flex justify-between pt-2 border-t">
            <span className="font-medium">Total</span>
            <span className="font-bold text-[var(--navy)]">{formatCurrency(total)}</span>
          </div>
          
          {/* Discount */}
          {discount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--navy)]">Discount</span>
              <span className="font-semibold text-[var(--navy)]">- {formatCurrency(discount)}</span>
            </div>
          )}
          
          {/* GST */}
          {!gstIncluded && gstPct > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">GST ({gstPct}%)</span>
              <span className="text-gray-600">{formatCurrency(gstAmount)}</span>
            </div>
          )}
          
          {/* Grand Total */}
          <div className="flex justify-between pt-2 border-t bg-[color-mix(in_srgb,var(--navy)_10%,white)] -mx-3 px-3 py-2 -mb-3 rounded-b-lg">
            <span className="font-bold">Grand Total</span>
            <span className="font-bold text-[var(--navy)]">{formatCurrency(grandTotal)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render a single card for total format
  const renderTotalAmountCard = (item, idx) => {
    const totalPackage = item.totalPackage || item.net_quoted_price || item.total_package_price || 0;
    const discount = item.discount || 0;
    const afterDiscount = totalPackage - discount;
    const gstBase = applyOn === 'markup' ? (item.markup_amount || 0) : afterDiscount;
    const gstAmount = gstPct > 0 ? gstBase * (gstPct / 100) : 0;
    // Ticket #285275 (Option A): included + stored GST > 0 means GST was added.
    const storedGst = Number(item.gst_amount || item.gstAmount || 0);
    const gstInTotal = gstIncluded && storedGst > 0;
    const grandTotal = (gstIncluded && !gstInTotal) ? afterDiscount : afterDiscount + (gstInTotal ? storedGst : gstAmount);
    
    const itemName = item.name || item.category || `Category ${idx + 1}`;
    
    return (
      <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="font-semibold text-[var(--navy)] mb-2">{itemName.replace(/_/g, ' ')}</div>
        
        {/* Breakup */}
        {showBreakup && (
          <div className="grid grid-cols-2 gap-2 text-sm mb-2 text-gray-600">
            <div>Hotel: {item.hotelCost > 0 ? formatCurrency(item.hotelCost) : 'Included'}</div>
            <div>Transfers: {item.transfers > 0 ? formatCurrency(item.transfers) : 'Included'}</div>
          </div>
        )}
        
        {/* Total Package */}
        <div className="flex justify-between items-center pt-2 border-t">
          <div>
            <span className="text-lg font-bold text-[var(--navy)]">{formatCurrency(totalPackage)}</span>
            <span className="text-xs text-gray-500 block">Total Package</span>
          </div>
        </div>
        
        {/* Discount */}
        {discount > 0 && (
          <div className="flex justify-between text-sm mt-2">
            <span className="text-[var(--navy)]">Discount</span>
            <span className="font-semibold text-[var(--navy)]">- {formatCurrency(discount)}</span>
          </div>
        )}
        
        {/* GST */}
        {!gstIncluded && gstPct > 0 && (
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-600">GST ({gstPct}%)</span>
            <span className="text-gray-600">{formatCurrency(gstAmount)}</span>
          </div>
        )}
        
        {/* Grand Total */}
        <div className="flex justify-between pt-2 mt-2 border-t bg-[color-mix(in_srgb,var(--navy)_10%,white)] -mx-3 px-3 py-2 -mb-3 rounded-b-lg">
          <span className="font-bold">
            Grand Total
            {gstIncluded && gstPct > 0 && <span className="text-xs font-normal block">incl. GST</span>}
          </span>
          <span className="font-bold text-[var(--navy)]">{formatCurrency(grandTotal)}</span>
        </div>
      </div>
    );
  };

  return (
    <div 
      className={`space-y-2 ${className}`}
      data-component="pricing-mobile-cards"
      data-type={type}
      data-testid="pricing-mobile-cards"
    >
      {/* Meal Plan Header (for meal_plan type) */}
      {type === 'meal_plan' && mealPlanLabel && (
        <div className="bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)] px-3 py-2 rounded-t-lg font-semibold -mb-1">
          {mealPlanLabel}
        </div>
      )}
      
      {/* Currency conversion note (for DMC) */}
      {type === 'dmc' && needsConversion && (
        <div className="text-xs text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_5%,white)] px-2 py-1 rounded">
          Converted from {currency} @ 1 {currency} = ₹{conversionRate.toFixed(2)} INR
          {markupPct > 0 && ` + ${markupPct}% markup`}
        </div>
      )}
      
      {/* Cards */}
      {items.map((item, idx) => 
        isPersonWise || type === 'dmc'
          ? renderPersonWiseCard(item, idx)
          : renderTotalAmountCard(item, idx)
      )}
    </div>
  );
};

export default PricingMobileCards;
